import { DataTable } from '@/components/dataTable';
import type { DataTableMeta } from '@/components/dataTable/data-table.utils';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import React from 'react';
import type { CustomerReferralStage1Row } from '../../hooks/useCustomerReferralStage1Report';
import { useCustomerReferralStage1Report } from '../../hooks/useCustomerReferralStage1Report';
import { getColumns } from './Column';

export function CustomerReferralStage1Table() {
  const { t } = useDynamicLocale();
  const { data, isLoading, pagination } = useCustomerReferralStage1Report();
  const columns = getColumns(t);
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const { table } = useDataTable<
    CustomerReferralStage1Row,
    DataTableMeta<CustomerReferralStage1Row> & Record<string, unknown>
  >({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.referralReports.tableTittle.customerReferReport'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + rowIndex + 1,
      actionKey: 'exportCsv',
      action: handleExport,
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row) => row.referralId,
    shallow: false,
    clearOnDefault: true,
  });

  const exportKeysByColumn = React.useMemo(
    () => ({
      sn: [t('common.page.tableColumn.sn')],
      referrerName: [t('common.page.tableColumn.referrerName')],
      reservedBy: [t('common.page.tableColumn.reservedBy')],
      reservationDateTime: [t('common.page.tableColumn.reservationDateTime')],
      pointsEarned: [t('common.page.tableColumn.pointsEarned')],
      stageDetails: [t('common.page.tableColumn.stageDetails')],
      createdDate: [t('common.page.tableColumn.receiptDateTime')],
    }),
    [t],
  );

  function handleExport() {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => ({
      [t('common.page.tableColumn.sn')]: getSerialNumber(table, index),
      [t('common.page.tableColumn.referrerName')]: row.referrerName,
      [t('common.page.tableColumn.reservedBy')]:
        row.reservedBy && row.reservedBy !== '-' ? row.reservedBy : row.reservedByNickname,
      [t('common.page.tableColumn.reservationDateTime')]: formatDate(row.reservationDateTime, 'YYYY/MM/DD HH:mm:ss'),
      [t('common.page.tableColumn.pointsEarned')]:
        row.pointsEarned === null ? '-' : `+${formatAmount(row.pointsEarned)}`,
      [t('common.page.tableColumn.stageDetails')]: row.stageDetails,
      [t('common.page.tableColumn.receiptDateTime')]:
        row.receiptDateTime === null ? '-' : formatDate(row.receiptDateTime, 'YYYY/MM/DD HH:mm:ss'),
    }));

    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, exportKeysByColumn);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'referral-report-stage-1',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  }

  return (
    <DataTable className="rounded-t-none" table={table}>
      <DataTableToolbar defaultDatePreset="today" hideViewOptions buttonText="common.page.table.exportCSV" />
    </DataTable>
  );
}
